import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Plots.css';

import { supabase } from '../supabaseClient';
import { isPriceInBudget } from '../utils/priceParser';

export default function Plots() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeFilter, setActiveFilter] = useState(location.state?.filter || 'all');
  const [locFilter, setLocFilter] = useState(location.state?.location || 'All Locations');
  const [budgetFilter, setBudgetFilter] = useState(location.state?.budget || 'Any Budget');
  const [typeFilter, setTypeFilter] = useState(location.state?.type || 'Any Type');
  const [selectedPlot, setSelectedPlot] = useState(null);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlots = async () => {
      setLoading(true);
      const { data } = await supabase.from('properties').select('*').eq('category', 'plots');
      if (data) setProperties(data);
      setLoading(false);
    };
    fetchPlots();
  }, []);

  useEffect(() => {
    if (location.state) {
      if (location.state.filter) setActiveFilter(location.state.filter);
      if (location.state.location) setLocFilter(location.state.location);
      if (location.state.budget) setBudgetFilter(location.state.budget);
      if (location.state.type) setTypeFilter(location.state.type);
    }
  }, [location.state]);

  const filteredPlots = properties.filter(plot => {
    const matchCategory = activeFilter === 'all' ? true : plot.subcategory === activeFilter;
    const matchLocation = (!locFilter || locFilter === 'All Locations') ? true : (plot.location && plot.location.includes(locFilter));
    const matchBudget = (!budgetFilter || budgetFilter === 'Any Budget') ? true : isPriceInBudget(plot.price, budgetFilter);
    const matchType = (!typeFilter || typeFilter === 'Any Type') ? true : (plot.sizes === typeFilter);
    return matchCategory && matchLocation && matchBudget && matchType;
  });

  return (
    <div className="plots-page">
      {/* Page Header */}
      <section className="page-header animate-fade-in-up">
        <div className="container text-center">
          <span className="section-tag" style={{ backgroundColor: 'var(--color-secondary-light)', color: 'var(--color-secondary)' }}>
            Invest In Land
          </span>
          <h1 className="page-title">Premium Plots</h1>
          <p className="page-subtitle">
            Secure your future wealth with our legally verified, high appreciation gated plotting developments in fast-growing growth corridors.
          </p>
        </div>
      </section>

      {/* Grid List */}
      <section className="plots-grid-section">
        <div className="container">
          {/* Filters */}
          <div className="filter-bar">
            {['all', 'open plots', 'villa plots', 'premium villa plots', 'investment plots'].map(filter => (
              <button 
                key={filter} 
                className={`filter-btn ${activeFilter === filter ? 'active' : ''}`}
                onClick={() => setActiveFilter(filter)}
              >
                {filter}
              </button>
            ))}
          </div>

          <div className="grid-3" style={{ alignItems: 'stretch' }}>
            {loading ? (
              <div style={{ textAlign: 'center', gridColumn: '1 / -1', padding: '40px 0' }}>
                <h3 style={{ color: 'var(--text-secondary)' }}>Loading...</h3>
              </div>
            ) : filteredPlots.length > 0 ? filteredPlots.map((plot) => (
              <div key={plot.id} className="premium-card plot-card flex-column" onClick={() => setSelectedPlot(plot)} style={{ cursor: 'pointer' }}>
                <div className="plot-card-image-container">
                  <img src={plot.image_url || plot.image} alt={plot.title} className="plot-card-image" />
                  <div className="plot-card-badges">
                    <span className="approval-badge">✓ {plot.approval}</span>
                    <span className={`status-badge status-plots`} style={plot.status === "Ready to Move In" ? { backgroundColor: '#ffca28', color: '#333', fontWeight: 'bold', textShadow: 'none' } : {}}>
                      {plot.status}
                    </span>
                  </div>
                </div>

                <div className="plot-card-body">
                  <h2 className="plot-title">{plot.title}</h2>
                  <p className="plot-location">📍 {plot.location}</p>

                  <div className="plot-specs">
                    <div className="spec-item">
                      <span className="spec-label">Plot Sizes</span>
                      <span className="spec-val font-bold">{plot.sizes}</span>
                    </div>
                    <div className="spec-item">
                      <span className="spec-label">Price Guide</span>
                      <span className="spec-val highlight-val">{plot.price}</span>
                    </div>
                  </div>

                  <div className="plot-card-footer" style={{ marginTop: 'auto', paddingTop: '15px' }}>
                    <button
                      className="btn btn-primary w-full"
                      style={{ marginBottom: '8px' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedPlot(plot);
                      }}
                    >
                      View Complete Info
                    </button>
                    <button
                      className="btn btn-secondary w-full"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.localStorage.setItem('enquiry_subject', `Enquiry about plots at ${plot.title}`);
                        navigate('/contact');
                      }}
                    >
                      Check Availability & Prices
                    </button>
                  </div>
                </div>
              </div>
            )) : (
              <div style={{ textAlign: 'center', gridColumn: '1 / -1', padding: '40px 0' }}>
                <h3 style={{ color: 'var(--text-secondary)' }}>No plots found matching your criteria.</h3>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Trust & Legality Section */}
      <section className="legality-banner">
        <div className="container">
          <div className="premium-card legality-card">
            <div className="grid-2">
              <div className="legality-info">
                <span className="section-tag">Zero Risk Investment</span>
                <h2>100% Legally Verified Projects</h2>
                <p>
                  Every plotting venture by Skyon Infra Developers goes through a rigorous legal audit. We guarantee:
                </p>
                <ul className="legality-list">
                  <li>✓ Spot registration of plots upon final payment</li>
                  <li>✓ Clear titles with 30 years link documents</li>
                  <li>✓ Fully approved layouts with layout permission numbers</li>
                  <li>✓ RERA registered projects complying with all regulations</li>
                </ul>
              </div>
              <div className="legality-action flex-center">
                <div className="text-center">
                  <p style={{ marginBottom: '20px', fontWeight: '500' }}>Want to check RERA details and legal documents?</p>
                  <button className="btn btn-primary" onClick={() => navigate('/contact')}>
                    Contact Legal Advisor
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modal */}
      {selectedPlot && (
        <div className="modal-overlay" onClick={() => setSelectedPlot(null)}>
          <div className="modal-content animate-fade-in-up" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedPlot(null)}>&times;</button>
            <img src={selectedPlot.image_url || selectedPlot.image} alt={selectedPlot.title} className="modal-image" />
            
            <div className="modal-body">
              <h2 className="modal-title">{selectedPlot.title}</h2>
              <p className="modal-tagline">{selectedPlot.tagline}</p>
              
              {selectedPlot.amenities?.length > 0 && (
                <div className="modal-section">
                  <h3>Amenities & Features</h3>
                  <ul className="grid-list">
                    {selectedPlot.amenities.map((feat, i) => <li key={i}>▪ {feat}</li>)}
                  </ul>
                </div>
              )}

              {selectedPlot.extended_details?.locationHighlights?.length > 0 && (
                <div className="modal-section">
                  <h3>Location & Connectivity</h3>
                  <ul className="grid-list">
                    {selectedPlot.extended_details.locationHighlights.map((loc, i) => <li key={i}>📍 {loc}</li>)}
                  </ul>
                </div>
              )}

              {selectedPlot.extended_details?.highlights?.length > 0 && (
                <div className="modal-section">
                  <h3>Project Highlights</h3>
                  <ul>
                    {selectedPlot.extended_details.highlights.map((h, i) => <li key={i}>✓ {h}</li>)}
                  </ul>
                </div>
              )}

              {selectedPlot.extended_details?.fullAmenities?.length > 0 && (
                <div className="modal-section">
                  <h3>Amenities & Features (Full)</h3>
                  <ul className="grid-list">
                    {selectedPlot.extended_details.fullAmenities.map((a, i) => <li key={i}>▪ {a}</li>)}
                  </ul>
                </div>
              )}

              <div className="modal-footer text-center">
                <button
                  className="btn btn-secondary w-full"
                  onClick={() => {
                    window.localStorage.setItem('enquiry_subject', `Enquiry about plots at ${selectedPlot.title}`);
                    setSelectedPlot(null);
                    navigate('/contact');
                  }}
                >
                  Enquire Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
